import { ReactNode } from 'react';

import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { Icon } from '../Icon';
import { validateCompoundComponentChildren } from '@/utils/validateCompoundComponentChildren';

interface PageHeaderProps {
  title: string;
  titleAdornment?: ReactNode; // Optional node to render inline with the title, e.g. for a badge or status indicator
  iconName?: string;
  subtitle?: string;
  children?: ReactNode; // Optional additional info or actions on the right side of the header
}

interface PageInfoComponentProps {
  children: ReactNode;
  className?: string;
}

const PageInfoComponent = ({ children, className }: PageInfoComponentProps) => {
  return <div className={className}>{children}</div>;
};

PageInfoComponent.displayName = 'PageInfo';

// Define allowed compound component display names
const ALLOWED_COMPOUND_COMPONENTS = ['PageInfo'];

export const PageHeader = ({
  title,
  titleAdornment,
  iconName,
  subtitle,
  children,
}: PageHeaderProps) => {
  // Validate children before rendering to only allow compound components
  if (children) {
    validateCompoundComponentChildren(
      children,
      'PageHeader',
      ALLOWED_COMPOUND_COMPONENTS,
    );
  }

  const theme = useTheme();
  const hidePageHeaderInfo = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          {iconName ? <Icon name={iconName} /> : null}
          <Typography component="h1" variant="h3SemiBold" color="primary">
            {title}
          </Typography>
          {!!titleAdornment && titleAdornment}
        </Stack>
        {!!subtitle && (
          <Typography color="textSecondary" component="h2" variant="h4">
            {subtitle}
          </Typography>
        )}
      </Stack>
      {!hidePageHeaderInfo && children}
    </Stack>
  );
};

PageHeader.Info = PageInfoComponent;
